import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import { createApiService } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import BotTradeHistory from "@/components/BotTradeHistory";
import BotAnalyticsPanel from "@/components/BotAnalyticsPanel";
import {
  Play,
  Pause,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Activity,
  Target,
  Zap,
  BarChart3,
  RefreshCw,
  Eye,
  Plus,
  Wallet,
  ArrowLeft,
  Settings,
  Search,
  CheckCircle,
  XCircle,
  MinusCircle,
  PlusCircle,
  Share2,
  Shield,
  Info,
  Skull,
  Trash2
} from "lucide-react";

// Icon mapper for live activity
const activityIcons = {
  "search": <Search className="w-4 h-4" />,
  "trending-up": <TrendingUp className="w-4 h-4" />,
  "trending-down": <TrendingDown className="w-4 h-4" />,
  "minus": <MinusCircle className="w-4 h-4" />,
  "zap": <Zap className="w-4 h-4" />,
  "plus-circle": <PlusCircle className="w-4 h-4" />,
  "check-circle": <CheckCircle className="w-4 h-4" />,
  "x-circle": <XCircle className="w-4 h-4" />,
  "alert-triangle": <AlertTriangle className="w-4 h-4" />,
  "eye": <Eye className="w-4 h-4" />
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return "—";
  }
};

// Short suffix identifying which engine ran the bot, e.g. "/hyper".
const botTypeSuffix = (t) => {
  const s = String(t || "").toLowerCase();
  if (s.includes("hyper")) return "/hyper";
  if (s.includes("poly")) return "/poly";
  if (s.includes("ai") || s.includes("claude")) return "/ai";
  if (s.includes("arb")) return "/arbitrage";
  return "/arbitrage";
};

export default function ArbitrageBotDashboard() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { isConnected, getAuthHeaders, addresses, signContractCall } = useWallet();
  const apiService = createApiService(getAuthHeaders);
  const activityScrollRef = useRef(null);

  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [liveActivities, setLiveActivities] = useState([]);
  const [marketsScanned, setMarketsScanned] = useState(0);
  const [ordersPlaced, setOrdersPlaced] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const { showUsd, toggleUsd, stxoshiUsd } = useCurrency();
  const [refreshFailed, setRefreshFailed] = useState(false);
  const settingsRef = useRef(null);
  const [showDeposit, setShowDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositLoading, setDepositLoading] = useState(false);
  const [verifyDepositLoading, setVerifyDepositLoading] = useState(false);
  
  // Position trading state (LONG and SHORT via ALEX DEX)
  const [showOpenPosition, setShowOpenPosition] = useState(false);
  const [positionAmount, setPositionAmount] = useState("");
  const [positionSide, setPositionSide] = useState("long"); // "long" or "short"
  const [positionLoading, setPositionLoading] = useState(false);
  
  // Withdraw state
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [closeAllLoading, setCloseAllLoading] = useState(false);
  
  // Strategy configuration state
  const [showStrategyConfig, setShowStrategyConfig] = useState(false);
  const [strategyType, setStrategyType] = useState("hybrid");
  const [momentumThreshold, setMomentumThreshold] = useState(0.15);
  const [meanReversionThreshold, setMeanReversionThreshold] = useState(0.5);
  const [latencyArbThreshold, setLatencyArbThreshold] = useState(0.1);
  const [minEdge, setMinEdge] = useState(0.5);
  const [strategyLoading, setStrategyLoading] = useState(false);
  
  // Settings state
  const [killSwitchPct, setKillSwitchPct] = useState(-5);
  const [takeProfitPct, setTakeProfitPct] = useState(10);
  
  // Poly-Copy whale scanning state
  const [walletsScanned, setWalletsScanned] = useState(0);
  const [whalesFound, setWhalesFound] = useState(0);
  const [scanningWallets, setScanningWallets] = useState([]);
  const [whaleActivities, setWhaleActivities] = useState([]);
  const [lastAlertedWhale, setLastAlertedWhale] = useState(null);
  const [whaleAlertCount, setWhaleAlertCount] = useState(0);

  // Load bot data
  const loadBot = useCallback(async (opts = {}) => {
    if (!botId) return;
    
    try {
      const response = await apiService.get(`/prediction-bots/${botId}`);
      if (response.data.success) {
        setRefreshFailed(false);
        setBot(response.data.bot);
        setKillSwitchPct(response.data.bot.config?.kill_switch_pct || -5);
        setTakeProfitPct(response.data.bot.config?.take_profit_pct || 10);
        
        // NOTE: markets/wallets scanned is sourced ONLY from the live-activity
        // endpoint (deterministic, backend-computed). Do NOT also compute it
        // here from elapsed seconds — that made the number bounce between two
        // different values on each refresh.
      }
    } catch (err) {
      console.error("Failed to load bot:", err);
      // "Failed to load bot data" toast intentionally suppressed per user
      // request; a subtle inline "reconnecting" dot is shown instead.
      setRefreshFailed(true);
      void opts;
    } finally {
      setLoading(false);
    }
  }, [botId, apiService]);

  // Fetch live activity stream
  const fetchLiveActivity = useCallback(async () => {
    if (!botId || bot?.status !== "running") return;
    
    try {
      const response = await apiService.get(`/prediction-bots/${botId}/live-activity`);
      if (response.data.success) {
        setMarketsScanned(response.data.markets_scanned || 0);
        setOrdersPlaced(response.data.orders_placed || 0);
        
        // Handle copy-bot (poly OR hyper) whale-scanning data
        const _copyTypes = ["poly_copy", "hyper_copy"];
        if (_copyTypes.includes(response.data.bot_type) || _copyTypes.includes(bot?.bot_type)) {
          setWalletsScanned(response.data.wallets_scanned || response.data.markets_scanned || 0);
          setWhalesFound(response.data.whales_found || 0);
          setWhaleActivities(response.data.whale_activities || []);
          
          // Update scanning wallets for animation
          const scanState = response.data.scanning_state;
          if (scanState?.recent_scans) {
            setScanningWallets(scanState.recent_scans.slice(0, 15));
          }
        }
        
        // Add new activities to the list with animation
        const newActivities = response.data.activities || [];
        setLiveActivities(prev => {
          // Filter out duplicates and keep last 50
          const existingIds = new Set(prev.map(a => a.id));
          const filtered = newActivities.filter(a => !existingIds.has(a.id));
          return [...filtered, ...prev].slice(0, 50);
        });
      }
    } catch (err) {
      console.error("Failed to fetch live activity:", err);
    }
  }, [botId, bot?.status, bot?.bot_type, apiService]);

  useEffect(() => {
    if (isConnected && botId) {
      loadBot();
    }
  }, [isConnected, botId, loadBot]);

  // Auto-refresh live activity when bot is running
  useEffect(() => {
    let interval;
    if (bot?.status === "running") {
      // Initial fetch
      fetchLiveActivity();
      // Then poll every 3 seconds
      interval = setInterval(fetchLiveActivity, 3000);
    }
    return () => clearInterval(interval);
  }, [bot?.status, fetchLiveActivity]);

  // Live balance/positions: silently refresh the bot doc while it is running so
  // the Balance card (Free/Locked) and Open Positions stay in sync with the
  // engine's per-tick trading (matches the "Live · updates each tick" badge).
  useEffect(() => {
    if (bot?.status !== "running") return;
    const id = setInterval(() => loadBot({ silent: true }), 12000);
    return () => clearInterval(id);
  }, [bot?.status, loadBot]);

  // Auto-scroll activity feed
  useEffect(() => {
    if (activityScrollRef.current) {
      activityScrollRef.current.scrollTop = 0;
    }
  }, [liveActivities]);

  // Whale Activity Alerts - Show toast notifications for significant whale moves
  useEffect(() => {
    if (!whaleActivities || whaleActivities.length === 0 || bot?.bot_type !== "poly_copy") return;
    
    // Check for new significant whale activities
    const latestActivity = whaleActivities[0];
    if (!latestActivity) return;
    
    // Only alert if this is a new whale we haven't alerted about
    const activityKey = `${latestActivity.wallet}-${latestActivity.timestamp}`;
    if (activityKey === lastAlertedWhale) return;
    
    // Only alert for significant trades ($5K+)
    const sizeUsd = latestActivity.size_usd || 0;
    if (sizeUsd < 5000) return;
    
    // Trigger toast notification for whale activity
    const isBuy = latestActivity.type === "buy";
    const walletShort = latestActivity.wallet_short || `${latestActivity.wallet?.slice(0, 6)}...${latestActivity.wallet?.slice(-4)}`;
    const marketName = latestActivity.market?.slice(0, 40) || "Unknown Market";
    
    if (isBuy) {
      toast.success(`🐋 Whale Alert: ${walletShort}`, {
        description: `BUY $${sizeUsd.toLocaleString()} on ${marketName}`,
        duration: 6000,
      });
    } else {
      toast.warning(`🐋 Whale Alert: ${walletShort}`, {
        description: `SELL $${sizeUsd.toLocaleString()} on ${marketName}`,
        duration: 6000,
      });
    }
    
    setLastAlertedWhale(activityKey);
    setWhaleAlertCount(prev => prev + 1);
  }, [whaleActivities, lastAlertedWhale, bot?.bot_type]);

  // Auto-poll to detect when deposit arrives (for "Awaiting funds" screen)
  useEffect(() => {
    // Only poll if bot exists, has zero balance, and we're not already loading
    if (bot && (bot.total_balance_stxoshi || 0) === 0 && botId && !loading) {
      const pollInterval = setInterval(() => {
        console.log("[BOT] Polling for deposit...");
        loadBot({ silent: true });
      }, 5000); // Check every 5 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [bot, botId, loading, loadBot]);

  const handleStartBot = async () => {
    setActionLoading(true);
    try {
      // Use poly-copy endpoint for poly_copy bots
      const endpoint = bot?.bot_type === "poly_copy" 
        ? `/prediction-bots/${botId}/start-poly-copy`
        : `/prediction-bots/${botId}/start`;
        
      const response = await apiService.post(endpoint);
      if (response.data.success) {
        if (bot?.bot_type === "poly_copy") {
          toast.success("Poly-Copy Bot started! Scanning whale wallets...");
          setLiveActivities([{
            id: "start-" + Date.now(),
            type: "system",
            icon: "eye",
            message: "Poly-Copy Bot started - scanning whale wallets...",
            details: "Connecting to Polymarket data feeds...",
            timestamp: new Date().toISOString(),
            priority: "high"
          }]);
        } else {
          toast.success("Bot started! Scanning markets...");
          setLiveActivities([{
            id: "start-" + Date.now(),
            type: "system",
            icon: "zap",
            message: "Bot started - initializing trading strategies...",
            details: "Connecting to market feeds...",
            timestamp: new Date().toISOString(),
            priority: "high"
          }]);
        }
        loadBot();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start bot");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopBot = async () => {
    setActionLoading(true);
    try {
      const response = await apiService.post(`/prediction-bots/${botId}/stop`);
      if (response.data.success) {
        toast.success("Bot stopped");
        setLiveActivities(prev => [{
          id: "stop-" + Date.now(),
          type: "system",
          icon: "pause",
          message: "Bot stopped - all positions will be managed manually",
          details: "Trading paused",
          timestamp: new Date().toISOString(),
          priority: "medium"
        }, ...prev]);
        loadBot();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to stop bot");
    } finally {
      setActionLoading(false);
    }
  };

  const handleKillBot = async () => {
    if (!window.confirm("Kill this bot permanently?\n\nThis will:\n• Auto-close ALL open positions and settle P&L\n• Return all funds to your available balance (withdraw them after)\n• Permanently stop trading — the bot CANNOT be restarted\n\nContinue?")) return;
    setActionLoading(true);
    try {
      const response = await apiService.post(`/prediction-bots/${botId}/kill`);
      if (response.data?.success) {
        toast.success(response.data.message || `Bot killed. ${response.data.freed_stxoshi?.toLocaleString?.() ?? ""} STXOSHI returned to available balance.`);
        loadBot();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to kill bot");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBot = async () => {
    if (!window.confirm("Delete this bot for good?\n\nThis removes the bot and its history from your list. Make sure you've WITHDRAWN any remaining balance first — it cannot be recovered after deletion.\n\nContinue?")) return;
    setActionLoading(true);
    try {
      const response = await apiService.delete(`/prediction-bots/${botId}`);
      if (response.data?.success) {
        toast.success("Bot deleted");
        navigate("/bots");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete bot");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setDepositLoading(true);
    try {
      // Step 1: Initiate deposit to get hot wallet address
      console.log("[DEPOSIT] Step 1: Initiating deposit for amount:", depositAmount);
      const initResponse = await apiService.post(`/prediction-bots/${botId}/deposit/initiate`, {
        amount: parseFloat(depositAmount)
      });

      if (!initResponse.data.success) {
        throw new Error(initResponse.data.message || "Failed to initiate deposit");
      }

      const { deposit_id, hot_wallet, stxoshi_contract, amount_stxoshi } = initResponse.data;
      console.log("[DEPOSIT] Got deposit_id:", deposit_id);
      console.log("[DEPOSIT] Hot wallet:", hot_wallet);
      console.log("[DEPOSIT] Contract:", stxoshi_contract);
      console.log("[DEPOSIT] Amount (micro):", amount_stxoshi);
      
      toast.info("Please approve the STXOSHI transfer in your wallet...");

      // Step 2: Request wallet transaction for SIP-010 token transfer
      // The STXOSHI contract: SP1Z92MPDQEWZXW36VX71Q25HKF5K2EPCJ304F275.tokensoft-token-v4karmaedcr
      const [contractAddress, contractName] = stxoshi_contract.split(".");
      console.log("[DEPOSIT] Contract address:", contractAddress);
      console.log("[DEPOSIT] Contract name:", contractName);
      
      // Import Clarity value constructors
      const { uintCV, principalCV, noneCV } = await import("@stacks/transactions");
      
      // Create the contract call using signContractCall from wallet context
      console.log("[DEPOSIT] Step 2: Calling signContractCall...");
      const txResult = await signContractCall({
        contractAddress,
        contractName,
        functionName: "transfer",
        functionArgs: [
          uintCV(amount_stxoshi),              // amount
          principalCV(addresses.stx),          // sender (from)
          principalCV(hot_wallet),             // recipient (to)
          noneCV()                             // memo (optional)
        ],
        postConditions: [],
        postConditionMode: "allow"  // Allow to avoid strict post-condition checks
      });

      console.log("[DEPOSIT] signContractCall result:", txResult);

      if (!txResult || !txResult.txid) {
        console.error("[DEPOSIT] No txid in result:", txResult);
        throw new Error("Transaction was cancelled or failed - no transaction ID returned");
      }

      console.log("[DEPOSIT] Got txid:", txResult.txid);
      toast.info("Transaction submitted! Verifying on-chain...");

      // Step 3: Confirm deposit with transaction ID
      // Wait longer for transaction to propagate and be indexed
      console.log("[DEPOSIT] Step 3: Waiting for transaction to be indexed...");
      
      // Try confirmation with retries
      let confirmSuccess = false;
      let lastError = null;
      
      for (let attempt = 1; attempt <= 3; attempt++) {
        console.log(`[DEPOSIT] Confirmation attempt ${attempt}/3`);
        
        // Wait longer on each attempt
        const waitTime = attempt * 5000; // 5s, 10s, 15s
        await new Promise(resolve => setTimeout(resolve, waitTime));

        try {
          const confirmResponse = await apiService.post(`/prediction-bots/${botId}/deposit/confirm`, {
            deposit_id,
            txid: txResult.txid
          });

          if (confirmResponse.data.success) {
            confirmSuccess = true;
            toast.success(`Deposited ${depositAmount} STXOSHI to bot!`);
            setShowDeposit(false);
            setDepositAmount("");
            loadBot();
            break;
          } else {
            lastError = confirmResponse.data.detail || "Verification pending";
          }
        } catch (confirmErr) {
          console.log(`[DEPOSIT] Confirmation attempt ${attempt} failed:`, confirmErr.response?.data?.detail || confirmErr.message);
          lastError = confirmErr.response?.data?.detail || confirmErr.message;
          
          // If it's a "not found" error, keep retrying
          if (lastError?.includes("not found") || lastError?.includes("pending")) {
            continue;
          }
          // For other errors, break
          break;
        }
      }
      
      if (!confirmSuccess) {
        // Transaction was signed but not yet confirmed
        toast.warning("Transaction submitted but not yet confirmed", {
          description: `TX: ${txResult.txid.slice(0, 16)}... Check back in a few minutes.`,
          duration: 10000
        });
        console.log("[DEPOSIT] Transaction pending, txid:", txResult.txid);
        console.log("[DEPOSIT] Last error:", lastError);
      }
    } catch (err) {
      console.error("[DEPOSIT] Error:", err);
      if (err.message?.includes("cancelled") || err.message?.includes("rejected")) {
        toast.error("Transaction cancelled");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Deposit failed");
      }
    } finally {
      setDepositLoading(false);
    }
  };

  // Verify Deposit Manually - Scan blockchain for unrecorded deposits
  const handleVerifyDeposit = async () => {
    setVerifyDepositLoading(true);
    try {
      toast.info("Scanning blockchain for deposits...");
      
      const response = await apiService.post(`/prediction-bots/${botId}/scan-deposits`);
      
      if (response.data.success) {
        // Backend returns { found, count, total_amount_stxoshi }. Keep the old
        // field names as fallbacks for backward-compat.
        const depositsFound = response.data.count ?? response.data.deposits_found ?? 0;
        const amountCredited = response.data.total_amount_stxoshi ?? response.data.amount_credited ?? 0;
        
        if (depositsFound > 0) {
          toast.success(`Found ${depositsFound} deposit(s)!`, {
            description: `Credited ${(amountCredited / 1_000_000).toLocaleString()} STXOSHI to your bot`,
            duration: 5000
          });
          loadBot(); // Refresh bot data
        } else {
          toast.info("No new deposits found", {
            description: "Your balance is up to date. Deposits may take a few minutes to appear on-chain.",
            duration: 5000
          });
        }
      } else {
        toast.error(response.data.message || "Verification failed");
      }
    } catch (err) {
      console.error("[VERIFY DEPOSIT] Error:", err);
      toast.error(err.response?.data?.detail || "Failed to verify deposits");
    } finally {
      setVerifyDepositLoading(false);
    }
  };

  // WITHDRAW STXOSHI - Real on-chain transfer back to user's wallet
  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    const amount = parseFloat(withdrawAmount);
    const available = (bot?.available_balance_stxoshi || 0) / 1_000_000;

    if (amount > available) {
      toast.error(`Insufficient available balance. Max: ${available.toLocaleString()} STXOSHI`);
      return;
    }

    setWithdrawLoading(true);
    try {
      toast.info("Processing withdrawal...");
      
      const response = await apiService.post(
        `/prediction-bots/${botId}/withdraw?amount=${amount}`
      );

      if (response.data.success) {
        if (response.data.is_real_transfer) {
          toast.success(`Withdrew ${amount.toLocaleString()} STXOSHI!`, {
            description: `TX: ${response.data.txid?.slice(0, 20)}... - Check your wallet!`
          });
        } else {
          toast.success(`Withdrew ${amount.toLocaleString()} STXOSHI from bot balance`);
        }
        setShowWithdraw(false);
        setWithdrawAmount("");
        loadBot();
      } else {
        toast.error(response.data.message || "Withdrawal failed");
      }
    } catch (err) {
      console.error("Withdraw error:", err);
      toast.error(err.response?.data?.detail || "Withdrawal failed");
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Save strategy configuration
  const handleSaveStrategy = async () => {
    setStrategyLoading(true);
    try {
      const response = await apiService.put(`/prediction-bots/${botId}/config`, {
        strategy: strategyType,
        min_edge_threshold: minEdge,
        momentum_threshold: momentumThreshold,
        mean_reversion_threshold: meanReversionThreshold,
        latency_arb_threshold: latencyArbThreshold
      });

      if (response.data.success) {
        toast.success("Strategy configuration saved!");
        setShowStrategyConfig(false);
        loadBot();
      } else {
        toast.error(response.data.message || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Strategy config error:", err);
      toast.error(err.response?.data?.detail || "Failed to save configuration");
    } finally {
      setStrategyLoading(false);
    }
  };

  // Open a REAL LONG position (SHORT disabled - ALEX swap not working)
  const handleOpenPosition = async () => {
    if (!positionAmount || parseFloat(positionAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    setPositionLoading(true);
    try {
      const response = await apiService.post(`/prediction-bots/${botId}/positions/open`, {
        side: positionSide, // LONG or SHORT
        amount_stxoshi: parseFloat(positionAmount)
      });

      if (response.data.success) {
        const sideLabel = positionSide.toUpperCase();
        const syntheticInfo = positionSide === "short" ? " (Synthetic)" : "";
        toast.success(`Opened ${sideLabel}${syntheticInfo} position @ $${response.data.position?.entry_price_usd?.toFixed(4) || '0.23'}`);
        setShowOpenPosition(false);
        setPositionAmount("");
        setPositionSide("long");
        loadBot();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open position");
    } finally {
      setPositionLoading(false);
    }
  };

  // Close a REAL position
  const handleClosePosition = async (positionId) => {
    setPositionLoading(true);
    try {
      const response = await apiService.post(`/prediction-bots/${botId}/positions/${positionId}/close`);

      if (response.data.success) {
        const pnl = response.data.pnl_pct;
        if (pnl >= 0) {
          toast.success(`Position closed: +${pnl.toFixed(2)}% profit!`);
        } else {
          toast.warning(`Position closed: ${pnl.toFixed(2)}% loss`);
        }
        loadBot();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to close position");
    } finally {
      setPositionLoading(false);
    }
  };

  // Close ALL open positions sequentially (frees locked capital → available)
  const handleCloseAll = async () => {
    const open = (bot?.open_positions || []).filter(p => p.status === "open");
    if (open.length === 0) {
      toast.info("No open positions to close");
      return;
    }
    if (!window.confirm(`Close all ${open.length} open position${open.length > 1 ? "s" : ""}? This settles P&L and frees the locked capital back into your available balance.`)) {
      return;
    }
    setCloseAllLoading(true);
    let closed = 0, failed = 0;
    toast.info(`Closing ${open.length} positions…`);
    for (const pos of open) {
      try {
        const res = await apiService.post(`/prediction-bots/${botId}/positions/${pos.id}/close`);
        if (res.data?.success) closed++; else failed++;
      } catch (e) {
        failed++;
      }
    }
    if (closed > 0) toast.success(`Closed ${closed} position${closed > 1 ? "s" : ""}. Locked capital freed to available.`);
    if (failed > 0) toast.error(`${failed} position${failed > 1 ? "s" : ""} could not be closed. Try again.`);
    setCloseAllLoading(false);
    loadBot();
  };

  const handleUpdateSettings = async () => {
    setActionLoading(true);
    try {
      const response = await apiService.patch(`/prediction-bots/${botId}`, {
        kill_switch_pct: killSwitchPct,
        take_profit_pct: takeProfitPct
      });
      if (response.data.success) {
        toast.success("Settings updated");
        setShowSettings(false);
        loadBot();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setActionLoading(false);
    }
  };

  // Calculate distance from thresholds
  const currentPnlPct = bot?.total_pnl_pct || 0;
  const STXOSHI_USD = stxoshiUsd || 0.0000037;
  const fmtPnl = (micro) => {
    const stx = (micro || 0) / 1_000_000;
    const sign = stx > 0 ? "+" : "";
    if (showUsd) {
      const usd = stx * STXOSHI_USD;
      const abs = Math.abs(usd);
      const dec = abs >= 1 ? 2 : abs >= 0.01 ? 4 : abs > 0 ? 8 : 2;
      return `${usd < 0 ? "-" : sign}$${Math.abs(usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: dec })}`;
    }
    return `${sign}${stx.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };
  const pnlUnit = showUsd ? "USD" : "STXOSHI";
  const distanceFromKillSwitch = Math.abs(killSwitchPct - currentPnlPct);
  const distanceFromTakeProfit = Math.abs(takeProfitPct - currentPnlPct);

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="p-6">
          <Card className="glass-card max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <Wallet className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">Connect Wallet</h2>
              <p className="text-muted-foreground">Please connect your wallet to view your bot.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="p-6">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="p-6">
          <Card className="glass-card max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">Bot Not Found</h2>
              <p className="text-muted-foreground mb-4">The bot you're looking for doesn't exist or you don't have access.</p>
              <Button onClick={() => navigate("/dashboard")} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const isRunning = bot.status === "running";
  const isKilled = bot.status === "killed";
  const statusColor = {
    running: "bg-green-500",
    stopped: "bg-gray-500",
    killed: "bg-red-500",
    profit_taken: "bg-blue-500",
    paused: "bg-yellow-500"
  }[bot.status] || "bg-gray-500";

  // Check if bot needs initial deposit (no balance available to trade/withdraw)
  const needsInitialDeposit = (bot.available_balance_stxoshi || 0) === 0 && (bot.total_balance_stxoshi || 0) === 0;

  // ZERO BALANCE STATE - Force user to deposit before trading
  if (needsInitialDeposit) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="p-4 md:p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Header Bar */}
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/dashboard")}
                className="text-muted-foreground"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-sm text-muted-foreground font-medium">Quantitative Trading Platform</h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const url = `${window.location.origin}/api/share/bot/${bot.id}`;
                  // X tweet intent with auto-generated text including the share URL
                  const sign = (bot.total_pnl_pct || 0) >= 0 ? "+" : "";
                  const text = `My STXOSHI bot ${bot.name ? `"${bot.name}" ` : ""}is at ${sign}${(bot.total_pnl_pct || 0).toFixed(2)}% over ${(bot.total_trades || 0).toLocaleString()} trades 🤖`;
                  const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
                  try { navigator.clipboard?.writeText(url); } catch (_e) { /* ignore */ }
                  toast.success("Share link copied — opening X…");
                  window.open(intent, "_blank", "noopener,noreferrer");
                }}
                className="text-muted-foreground hover:text-secondary"
                data-testid="bot-share-button"
                title="Share this bot's performance card"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
            </div>

            {/* Bot Info Card */}
            <Card className="glass-card border-border">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-3 h-3 rounded-full ${statusColor} animate-pulse`} />
                  <div>
                    <h2 className="font-heading text-xl font-bold">
                      {bot.name ? `Bot ${bot.name}` : "Arbitrage Prediction Bot"}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Deployed successfully</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Checking for deposit...
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* DEPOSIT REQUIRED OVERLAY */}
            <Card className="glass-card border-2 border-primary/50 bg-primary/5" data-testid="deposit-required-card">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Wallet className="w-10 h-10 text-primary" />
                </div>
                
                <h2 className="font-heading text-2xl font-bold mb-3">
                  Deposit STXOSHI to Start Trading
                </h2>
                
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Your bot is deployed and ready! Transfer STXOSHI tokens from your wallet to activate 
                  automated trading features and start capturing arbitrage opportunities.
                </p>

                {/* Deposit Form */}
                <Card className="bg-muted/30 border-border max-w-md mx-auto mb-6">
                  <CardContent className="p-4 md:p-6">
                    <Label className="text-left block mb-2">Amount to Deposit</Label>
                    <div className="flex gap-3">
                      <Input
                        type="number"
                        placeholder="e.g., 1000000"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        className="flex-1 text-lg"
                        data-testid="initial-deposit-amount-input"
                      />
                      <Button 
                        onClick={handleDeposit}
                        disabled={depositLoading || !depositAmount}
                        className="bg-primary hover:bg-primary/90 px-6"
                        size="lg"
                        data-testid="initial-deposit-btn"
                      >
                        {depositLoading ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Wallet className="w-4 h-4 mr-2" />
                            Deposit
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 text-left">
                      Your Xverse wallet will prompt you to approve the STXOSHI transfer.
                    </p>
                  </CardContent>
                </Card>

                {/* Quick Amount Buttons */}
                <div className="flex justify-center gap-2 mb-6">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDepositAmount("100000")}
                  >
                    100K
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDepositAmount("500000")}
                  >
                    500K
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDepositAmount("1000000")}
                  >
                    1M
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDepositAmount("5000000")}
                  >
                    5M
                  </Button>
                </div>

                {/* Info Note */}
                <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg text-left max-w-md mx-auto">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Important</p>
                    <p>
                      STXOSHI deposits are transferred to the bot's managed wallet for trading. 
                      You can withdraw anytime from the bot dashboard.
                    </p>
                  </div>
                </div>

                {/* Manual Deposit Check */}
                <div className="mt-6 text-center">
                  <p className="text-xs text-muted-foreground mb-2">
                    Already deposited but stuck on this screen?
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      try {
                        toast.info("Checking blockchain for deposits...");
                        // Call admin verify endpoint to scan for deposits
                        const response = await apiService.post(`/prediction-bots/${botId}/scan-deposits`);
                        if (response.data.success && response.data.found) {
                          toast.success(`Found deposit! ${response.data.amount_display?.toLocaleString()} STXOSHI credited.`);
                          loadBot();
                        } else {
                          toast.info("No new deposits found on-chain. Please wait a few minutes.");
                          loadBot();
                        }
                      } catch (err) {
                        console.error("Scan deposits error:", err);
                        toast.error("Could not verify deposits. Try again.");
                      }
                    }}
                    className="text-sm bg-cyan-600 hover:bg-cyan-500 text-white font-semibold px-4 py-2 shadow-lg shadow-cyan-500/30 border border-cyan-400/50 animate-pulse"
                    data-testid="manual-verify-deposit-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Verify Deposit Manually
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* What happens next */}
            <Card className="glass-card border-border">
              <CardContent className="p-4 md:p-6">
                <h3 className="font-heading font-semibold mb-4">What happens next?</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">1</div>
                    <p className="text-sm">Deposit STXOSHI tokens to fund your bot</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">2</div>
                    <p className="text-sm text-muted-foreground">Configure risk parameters (kill switch, take profit)</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">3</div>
                    <p className="text-sm text-muted-foreground">Start trading - bot scans markets and opens positions</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">4</div>
                    <p className="text-sm text-muted-foreground">Monitor live activity and manage positions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="p-4 md:p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header Bar */}
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/dashboard")}
              className="text-muted-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-sm text-muted-foreground font-medium">Quantitative Trading Platform</h1>
            <Button 
              variant="ghost" 
              size="icon"
              data-testid="bot-settings-toggle"
              onClick={() => {
                const next = !showSettings;
                setShowSettings(next);
                // Panel lives at the bottom of the page — scroll it into view
                // on open so the gear doesn't feel like a dead button.
                if (next) requestAnimationFrame(() => settingsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
              }}
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>

          {/* Top Stats Bar — 2-up on mobile, 4-up on tablets+ */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Card className="glass-card">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  Total P&L
                  <button
                    onClick={toggleUsd}
                    className="text-[9px] px-1 rounded border border-border hover:bg-muted"
                    data-testid="bot-pnl-unit-toggle"
                  >
                    {showUsd ? "USD" : "STXOSHI"}
                  </button>
                </p>
                <p className={`font-mono text-lg font-bold ${currentPnlPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {fmtPnl(bot.total_pnl_stxoshi)}
                </p>
                <p className="text-[10px] text-muted-foreground">{pnlUnit}</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <Activity className="w-3 h-3" /> Active
                </p>
                <p className="font-mono text-lg font-bold">
                  {bot.open_positions?.filter(p => p.status === "open").length || 0}
                </p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Positions
                </p>
                <p className="font-mono text-lg font-bold">
                  {bot.open_positions?.length || 0}
                </p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Win Rate</p>
                <p className="font-mono text-lg font-bold text-primary">
                  {(bot.win_rate || 0).toFixed(1)}%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Bot Card */}
          <Card className="glass-card border-border">
            <CardContent className="p-4 md:p-6">
              {/* Status Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-3 h-3 rounded-full ${statusColor} ${isRunning ? 'animate-pulse' : ''}`} />
                <div>
                  <h2 className="font-heading text-xl font-bold">
                    {bot.name ? `Bot ${bot.name}` : "Arbitrage Prediction Bot Hunter"}
                  </h2>
                  <p className="text-sm text-muted-foreground uppercase">
                    {bot.status.toUpperCase()} - Cycle #{bot.cycles_completed || 0}
                  </p>
                </div>
                {refreshFailed && (
                  <span className="ml-auto flex items-center gap-1 text-[10px] text-amber-400" data-testid="bot-reconnecting" title="Live refresh hiccup — retrying">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                    reconnecting…
                  </span>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Current P&L */}
                <Card className="bg-muted/30 border-border">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground mb-2">Current P&L</p>
                    <p className={`font-mono text-2xl md:text-3xl font-bold ${currentPnlPct >= 0 ? 'text-foreground' : 'text-red-400'}`}>
                      {fmtPnl(bot.total_pnl_stxoshi)} <span className="text-sm">{pnlUnit}</span>
                    </p>
                    <p className={`text-sm ${currentPnlPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {currentPnlPct >= 0 ? '+' : ''}{currentPnlPct.toFixed(2)}%
                    </p>
                  </CardContent>
                </Card>

                {/* Kill Switch */}
                <Card className="bg-muted/30 border-border">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <TrendingDown className="w-4 h-4" /> Kill Switch
                    </p>
                    <p className="font-mono text-2xl md:text-3xl font-bold text-red-400">
                      {Math.abs(killSwitchPct).toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground">
                      away from {killSwitchPct}%
                    </p>
                  </CardContent>
                </Card>

                {/* Take Profit */}
                <Card className="bg-muted/30 border-border">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" /> Take Profit
                    </p>
                    <p className="font-mono text-2xl md:text-3xl font-bold text-green-400">
                      {takeProfitPct.toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground">
                      to +{takeProfitPct}%
                    </p>
                  </CardContent>
                </Card>

                {/* Trades This Cycle */}
                <Card className="bg-muted/30 border-border">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground mb-2">Trades This Cycle</p>
                    <p className="font-mono text-2xl md:text-3xl font-bold">
                      {bot.total_trades || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">automated</p>
                  </CardContent>
                </Card>
              </div>

              {/* Start/Stop Button */}
              <Button
                onClick={isRunning ? handleStopBot : handleStartBot}
                disabled={actionLoading || isKilled}
                className={`w-full h-14 text-lg font-semibold ${
                  isRunning 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-green-500 hover:bg-green-600'
                } text-white`}
                data-testid={isRunning ? "stop-bot-btn" : "start-bot-btn"}
              >
                {actionLoading ? (
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                ) : isRunning ? (
                  <Pause className="w-5 h-5 mr-2" />
                ) : (
                  <Play className="w-5 h-5 mr-2" />
                )}
                {isKilled ? "Bot Killed" : isRunning ? "Stop Trading" : "Start Trading"}
              </Button>

              {/* Kill / Delete — permanent lifecycle actions */}
              <div className="flex gap-2 mt-3">
                {!isKilled && (
                  <Button
                    onClick={handleKillBot}
                    disabled={actionLoading}
                    variant="outline"
                    className="flex-1 border-red-500/40 text-red-400 hover:bg-red-500/10"
                    data-testid="kill-bot-btn"
                    title="Permanently stop, settle all positions, and return funds to your available balance"
                  >
                    <Skull className="w-4 h-4 mr-2" /> Kill Bot
                  </Button>
                )}
                {!isRunning && (
                  <Button
                    onClick={handleDeleteBot}
                    disabled={actionLoading}
                    variant="outline"
                    className="flex-1 border-border text-muted-foreground hover:bg-muted"
                    data-testid="delete-bot-btn"
                    title="Remove this bot and its history (withdraw any balance first)"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                )}
              </div>
              {!isKilled && (
                <p className="text-[11px] text-muted-foreground/70 mt-2 text-center">
                  Kill = permanent stop + auto-close all positions + funds back to available balance
                </p>
              )}
            </CardContent>
          </Card>

          {/* Live Trading Activity */}
          <Card className="glass-card border-border">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  Live Trading Activity
                </h3>
                <div className="flex items-center gap-2">
                  {isRunning && (
                    <Badge variant="outline" className="text-green-400 border-green-400">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse mr-2" />
                      Live
                    </Badge>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowDeposit(!showDeposit)}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Deposit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowWithdraw(!showWithdraw)}
                    className="text-xs border-primary/50 text-primary"
                    data-testid="withdraw-btn"
                  >
                    <Wallet className="w-3 h-3 mr-1" />
                    Withdraw
                  </Button>
                </div>
              </div>

              {/* Deposit Modal */}
              {showDeposit && (
                <Card className="bg-secondary/10 border-secondary/30 mb-4 animate-fade-in">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      Deposit STXOSHI
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Transfer STXOSHI from your wallet to the bot's trading balance.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Amount (e.g., 1000000)"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleDeposit}
                        disabled={depositLoading || !depositAmount}
                        className="bg-secondary hover:bg-secondary/90"
                      >
                        {depositLoading ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          "Deposit"
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Your wallet will prompt you to approve the transfer.
                    </p>
                    <p className="text-xs text-amber-400/90 mt-2 flex items-start gap-1.5" data-testid="deposit-destination-note">
                      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>These funds go to <span className="font-semibold">this bot's trading balance</span> — not your User Wallet. The two balances are separate.</span>
                    </p>
                    
                    {/* Verify Deposit Manually Button - Blue Pulsing */}
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-2">
                        Already deposited but balance not updated?
                      </p>
                      <Button
                        onClick={handleVerifyDeposit}
                        disabled={verifyDepositLoading}
                        className="w-full bg-cyan-500 hover:bg-cyan-600 text-white animate-pulse"
                        data-testid="verify-deposit-btn"
                      >
                        {verifyDepositLoading ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Verify Deposit Manually
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Withdraw Section */}
              {showWithdraw && (
                <Card className="bg-primary/5 border-primary/30 mb-4">
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-primary" />
                      Withdraw STXOSHI
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Transfer STXOSHI from the bot back to your wallet (real on-chain transfer).
                    </p>
                    {(() => {
                      const free = (bot?.available_balance_stxoshi || 0) / 1_000_000;
                      const locked = (bot?.locked_balance_stxoshi || 0) / 1_000_000;
                      const maxW = ((bot?.withdrawable_stxoshi ?? bot?.available_balance_stxoshi) || 0) / 1_000_000;
                      const amt = parseFloat(withdrawAmount) || 0;
                      const remaining = Math.max(0, free - amt);
                      const overMax = amt > maxW + 1e-9;
                      const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });
                      return (
                        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 mb-3 text-xs space-y-1.5" data-testid="withdraw-preview">
                          <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground">Max withdrawable now</span>
                            <span className="font-mono text-green-400" data-testid="withdraw-max">{fmt(maxW)} STXOSHI</span>
                          </div>
                          {locked > 0 && (
                            <div className="flex justify-between gap-2">
                              <span className="text-muted-foreground">Locked in open positions</span>
                              <span className="font-mono text-amber-400">{fmt(locked)} STXOSHI</span>
                            </div>
                          )}
                          {amt > 0 && (
                            <>
                              <div className="flex justify-between gap-2 pt-1.5 border-t border-border/50">
                                <span className="text-muted-foreground">You'll receive</span>
                                <span className="font-mono text-foreground">{fmt(Math.min(amt, free))} STXOSHI</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-muted-foreground">Remaining in bot</span>
                                <span className="font-mono text-foreground">{fmt(remaining)} STXOSHI</span>
                              </div>
                            </>
                          )}
                          {locked > 0 && (
                            <p className="text-[11px] text-muted-foreground pt-1">
                              Close open positions to free the locked {fmt(locked)} STXOSHI into your withdrawable balance.
                            </p>
                          )}
                          {overMax && (
                            <p className="text-[11px] text-amber-400 pt-1" data-testid="withdraw-over-max-warning">
                              Amounts above the max are held for a quick manual review before payout.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    <div className="flex gap-2 mb-2">
                      <Input
                        type="number"
                        placeholder="Amount to withdraw"
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        className="flex-1"
                        data-testid="withdraw-amount-input"
                      />
                      <Button 
                        onClick={handleWithdraw}
                        disabled={withdrawLoading || !withdrawAmount}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="confirm-withdraw-btn"
                      >
                        {withdrawLoading ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          "Withdraw"
                        )}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setWithdrawAmount((((bot?.withdrawable_stxoshi ?? bot?.available_balance_stxoshi) || 0) / 1_000_000).toString())}
                        className="text-xs"
                        data-testid="withdraw-max-btn"
                      >
                        Max
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setWithdrawAmount((((bot?.withdrawable_stxoshi ?? bot?.available_balance_stxoshi) || 0) / 2_000_000).toString())}
                        className="text-xs"
                      >
                        50%
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      This executes a real on-chain STXOSHI transfer to your wallet.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Stats Grid - Different for Poly-Copy vs Arbitrage bots */}
              {bot?.bot_type === "poly_copy" ? (
                <>
                  {/* Poly-Copy specific stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="bg-purple-500/10 border-purple-500/30">
                      <CardContent className="p-3">
                        <p className="text-xs text-purple-400">Wallets Scanned</p>
                        <p className="font-mono text-xl font-bold">{walletsScanned.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-500/10 border-purple-500/30">
                      <CardContent className="p-3">
                        <p className="text-xs text-purple-400">Whales Found</p>
                        <p className="font-mono text-xl font-bold">{whalesFound}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Animated Wallet Scanning Display - Only when running */}
                  {isRunning && (
                    <Card className="bg-black/40 border-purple-500/50 mb-4 overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Eye className="w-4 h-4 text-purple-400 animate-pulse" />
                          <span className="text-sm font-medium text-purple-400">Scanning Whale Wallets...</span>
                          <div className="ml-auto flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-purple-400 animate-ping" />
                            <span className="text-xs text-muted-foreground">LIVE</span>
                          </div>
                        </div>
                        
                        {/* Fast scrolling wallet addresses */}
                        <div className="relative h-32 overflow-hidden font-mono text-xs">
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80 z-10" />
                          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-black/80 to-transparent z-10" />
                          
                          <div className="space-y-1 animate-scroll-wallets">
                            {(scanningWallets.length > 0 
                              ? scanningWallets 
                              : Array.from({length: 15}, (_, i) => ({
                                  address: `0x${Math.random().toString(16).slice(2,42)}`,
                                  is_whale: Math.random() > 0.85
                                }))
                            ).map((wallet, idx) => (
                              <div 
                                key={idx}
                                className={`flex items-center gap-2 px-2 py-1 rounded transition-all ${
                                  wallet.is_whale 
                                    ? 'bg-purple-500/30 text-purple-300 animate-flash' 
                                    : 'text-gray-500'
                                }`}
                              >
                                <span className={`w-2 h-2 rounded-full ${wallet.is_whale ? 'bg-purple-400' : 'bg-gray-600'}`} />
                                <span className="truncate">{wallet.address}</span>
                                {wallet.is_whale && (
                                  <span className="ml-auto text-purple-400 text-[10px] uppercase font-bold">WHALE</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-purple-500/20">
                          <span>14,000+ wallets in database</span>
                          <span>{whalesFound} whales detected</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                /* Regular Arbitrage Bot stats */
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <Card className="bg-muted/30 border-border">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">Markets Scanned</p>
                      <p className="font-mono text-xl font-bold">{marketsScanned.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30 border-border">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">Orders Placed</p>
                      <p className="font-mono text-xl font-bold">{ordersPlaced || bot?.total_trades || 0}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Live Activity Feed with Scrolling */}
              <ScrollArea className="h-64" ref={activityScrollRef}>
                <div className="space-y-2 pr-4">
                  {liveActivities.length === 0 ? (
                    <div className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg animate-pulse">
                      <Search className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {isRunning ? "Initializing market feeds..." : "Bot is stopped. Start trading to see activity."}
                      </span>
                      {isRunning && <Badge variant="outline" className="text-xs">system</Badge>}
                    </div>
                  ) : (
                    liveActivities.map((activity, idx) => {
                      const priorityColors = {
                        critical: "border-l-red-500 bg-red-500/10",
                        high: "border-l-primary bg-primary/5",
                        medium: "border-l-secondary bg-secondary/5",
                        low: "border-l-border bg-muted/20"
                      };
                      const iconColor = {
                        critical: "text-red-400",
                        high: "text-primary",
                        medium: "text-secondary",
                        low: "text-muted-foreground"
                      };
                      
                      return (
                        <div 
                          key={activity.id || idx} 
                          className={`flex items-start gap-3 p-3 rounded-lg border-l-4 transition-all ${
                            priorityColors[activity.priority] || priorityColors.low
                          } ${idx === 0 ? 'animate-slide-in' : ''}`}
                        >
                          <div className={`mt-0.5 ${iconColor[activity.priority] || iconColor.low}`}>
                            {activityIcons[activity.icon] || <Eye className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{activity.message}</p>
                            {activity.details && (
                              <p className="text-xs text-muted-foreground mt-0.5">{activity.details}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                activity.type === "position_opened" || activity.type === "opportunity_found"
                                  ? "text-primary border-primary"
                                  : activity.type === "position_closed"
                                    ? activity.pnl_positive ? "text-green-400 border-green-400" : "text-red-400 border-red-400"
                                    : ""
                              }`}
                            >
                              {activity.type?.replace(/_/g, " ")}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(activity.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Strategy Monitor */}
          <Card className="glass-card border-border">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold">Strategy Monitor</h3>
                <Button 
                  variant="default" 
                  size="sm"
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={() => setShowStrategyConfig(!showStrategyConfig)}
                >
                  {showStrategyConfig ? "Close" : "Initialize Strategies"}
                </Button>
              </div>

              {/* Strategy Configuration Panel */}
              {showStrategyConfig ? (
                <div className="space-y-4 bg-muted/30 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Strategy Configuration</h4>
                  
                  {/* Strategy Type */}
                  <div>
                    <Label className="text-sm">Strategy Type</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <Button
                        variant={strategyType === "momentum" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setStrategyType("momentum")}
                      >
                        Momentum
                      </Button>
                      <Button
                        variant={strategyType === "mean_reversion" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setStrategyType("mean_reversion")}
                      >
                        Mean Rev.
                      </Button>
                      <Button
                        variant={strategyType === "hybrid" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setStrategyType("hybrid")}
                      >
                        Hybrid
                      </Button>
                    </div>
                  </div>

                  {/* Min Edge Threshold */}
                  <div>
                    <div className="flex justify-between">
                      <Label className="text-sm">Min Edge Threshold</Label>
                      <span className="text-sm font-mono text-primary">{minEdge.toFixed(2)}%</span>
                    </div>
                    <Slider
                      value={[minEdge]}
                      onValueChange={([v]) => setMinEdge(v)}
                      min={0.1}
                      max={2.0}
                      step={0.1}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Lower = more trades, higher = fewer but stronger signals
                    </p>
                  </div>

                  {/* Momentum Threshold */}
                  <div>
                    <div className="flex justify-between">
                      <Label className="text-sm">Momentum Trigger</Label>
                      <span className="text-sm font-mono">{momentumThreshold.toFixed(2)}%</span>
                    </div>
                    <Slider
                      value={[momentumThreshold]}
                      onValueChange={([v]) => setMomentumThreshold(v)}
                      min={0.05}
                      max={1.0}
                      step={0.05}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      5-min price change to trigger momentum signal
                    </p>
                  </div>

                  {/* Mean Reversion Threshold */}
                  <div>
                    <div className="flex justify-between">
                      <Label className="text-sm">Mean Reversion Trigger</Label>
                      <span className="text-sm font-mono">{meanReversionThreshold.toFixed(2)}%</span>
                    </div>
                    <Slider
                      value={[meanReversionThreshold]}
                      onValueChange={([v]) => setMeanReversionThreshold(v)}
                      min={0.2}
                      max={3.0}
                      step={0.1}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      15-min price change to trigger mean reversion
                    </p>
                  </div>

                  {/* Latency Arb Threshold */}
                  <div>
                    <div className="flex justify-between">
                      <Label className="text-sm">Latency Arb Trigger</Label>
                      <span className="text-sm font-mono">{latencyArbThreshold.toFixed(2)}%</span>
                    </div>
                    <Slider
                      value={[latencyArbThreshold]}
                      onValueChange={([v]) => setLatencyArbThreshold(v)}
                      min={0.05}
                      max={0.5}
                      step={0.05}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      1-min price change to trigger latency arb
                    </p>
                  </div>

                  <Button
                    className="w-full bg-green-500 hover:bg-green-600"
                    onClick={handleSaveStrategy}
                    disabled={strategyLoading}
                  >
                    {strategyLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Save Strategy Configuration
                  </Button>
                </div>
              ) : (
                <div className="bg-muted/30 rounded-lg p-6 text-center">
                  <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {bot.config?.strategy === "hybrid" 
                      ? "Hybrid strategy active. Combines momentum, mean reversion, and latency arbitrage."
                      : `${(bot.config?.strategy || "hybrid").replace("_", " ")} strategy active.`}
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-muted-foreground">Min Edge</p>
                      <p className="font-mono font-bold">{bot.config?.min_edge_threshold || 0.5}%</p>
                    </div>
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-muted-foreground">Trade Interval</p>
                      <p className="font-mono font-bold">{bot.config?.trade_interval_seconds || 30}s</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Live Trading Section */}
          <Card className="glass-card border-border">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Live Trading
                </h3>
                {isRunning && (
                  <Badge variant="outline" className="text-green-400 border-green-400">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse mr-2" />
                    Live
                  </Badge>
                )}
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                Wallet: {addresses.stx?.slice(0, 6)}...{addresses.stx?.slice(-6)}
              </p>

              {/* Open Position Button */}
              <Button 
                className="w-full bg-blue-500 hover:bg-blue-600 mb-4"
                onClick={() => setShowOpenPosition(!showOpenPosition)}
                disabled={bot.status !== "running" || positionLoading}
              >
                <Plus className="w-4 h-4 mr-2" />
                + Open Real Position
              </Button>

              {/* Open Position Form */}
              {showOpenPosition && (
                <Card className="bg-secondary/10 border-secondary/30 mb-4 animate-fade-in">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      {positionSide === "long" ? (
                        <TrendingUp className="w-4 h-4 text-green-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-400" />
                      )}
                      Open {positionSide.toUpperCase()} Position
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      {positionSide === "long" 
                        ? "Go LONG on STXOSHI - profit when value increases." 
                        : "Go SHORT on STXOSHI - profit when value decreases. Uses ALEX DEX swap."}
                    </p>
                    
                    {/* LONG/SHORT Toggle */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button
                        className={`p-3 rounded-lg border transition-all ${
                          positionSide === "long"
                            ? "bg-green-500/20 border-green-500 text-green-400"
                            : "bg-muted/30 border-border text-muted-foreground hover:border-green-500/50"
                        }`}
                        onClick={() => setPositionSide("long")}
                      >
                        <TrendingUp className="w-5 h-5 mx-auto mb-1" />
                        <p className="text-sm font-medium">LONG</p>
                        <p className="text-xs opacity-70">Profit when price rises</p>
                      </button>
                      <button
                        className={`p-3 rounded-lg border transition-all ${
                          positionSide === "short"
                            ? "bg-red-500/20 border-red-500 text-red-400"
                            : "bg-muted/30 border-border text-muted-foreground hover:border-red-500/50"
                        }`}
                        onClick={() => setPositionSide("short")}
                      >
                        <TrendingDown className="w-5 h-5 mx-auto mb-1" />
                        <p className="text-sm font-medium">SHORT</p>
                        <p className="text-xs opacity-70">Profit when price falls</p>
                      </button>
                    </div>

                    {/* SHORT info - Synthetic Short explanation */}
                    {positionSide === "short" && (
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2 mb-3 text-xs">
                        <p className="text-orange-400 font-medium">Synthetic SHORT Position</p>
                        <p className="text-muted-foreground">
                          Your STXOSHI is locked. Profit when price DROPS, loss when price rises.
                          P&L settles in real STXOSHI from hot wallet.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Amount STXOSHI"
                        value={positionAmount}
                        onChange={(e) => setPositionAmount(e.target.value)}
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleOpenPosition}
                        disabled={positionLoading || !positionAmount}
                        className={positionSide === "long" 
                          ? "bg-green-500 hover:bg-green-600" 
                          : "bg-red-500 hover:bg-red-600"}
                      >
                        {positionLoading ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            {positionSide === "long" ? (
                              <TrendingUp className="w-4 h-4 mr-1" />
                            ) : (
                              <TrendingDown className="w-4 h-4 mr-1" />
                            )}
                            Open {positionSide === "long" ? "Long" : "Short"}
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Available: {((bot.available_balance_stxoshi || 0) / 1_000_000).toLocaleString()} STXOSHI
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Open Positions with Close Buttons */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium">Open Positions ({bot.open_positions?.filter(p => p.status === "open").length || 0})</h4>
                    {bot.status === "running" && (
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="bot-positions-live">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                        </span>
                        Live
                      </span>
                    )}
                  </div>
                  {bot.open_positions?.filter(p => p.status === "open").length > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleCloseAll}
                      disabled={closeAllLoading || positionLoading}
                      className="text-xs h-7 px-3"
                      data-testid="bot-close-all-btn"
                    >
                      {closeAllLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Close All"}
                    </Button>
                  )}
                </div>
                <Card className="bg-muted/30 border-border">
                  <CardContent className="p-4">
                    {bot.open_positions?.filter(p => p.status === "open").length > 0 
                      ? bot.open_positions.filter(p => p.status === "open").map(pos => {
                          const asset = pos.price_asset || "STXOSHI";
                          const rawPct = pos.pnl_pct || 0;
                          const entryValid = (pos.entry_price != null && pos.entry_price > 0) || (pos.entry_price_usd != null && pos.entry_price_usd > 0);
                          // A |%| at/above the 100 cap means the entry/current
                          // prices are on mismatched scales (legacy/mixed-asset)
                          // and the % is not meaningful — show a dash instead.
                          const pctReliable = entryValid && Math.abs(rawPct) < 100;
                          return (
                          <div key={pos.id} className="flex flex-wrap justify-between items-center gap-y-1 py-2 border-b border-border last:border-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${pos.side === "long" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                                {pos.side?.toUpperCase()}
                              </span>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-foreground/80 shrink-0" data-testid="position-asset">
                                {asset}
                              </span>
                              {pos.manual && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/40 shrink-0 flex items-center gap-1" data-testid="position-manual-badge" title="You opened this manually — the bot will not auto-close it">
                                  <Shield className="w-2.5 h-2.5" /> Manual
                                </span>
                              )}
                              <span className="text-xs sm:text-sm truncate">{(pos.amount_stxoshi / 1_000_000).toLocaleString()} STXOSHI</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="text-right">
                                {pctReliable ? (
                                  <span className={`text-sm font-mono ${rawPct >= 0 ? "text-green-400" : "text-red-400"}`}>
                                    {rawPct >= 0 ? "+" : ""}{rawPct.toFixed(2)}%
                                  </span>
                                ) : (
                                  <span className="text-sm font-mono text-muted-foreground" title="P&L not available yet (awaiting a valid mark price)">—</span>
                                )}
                                <p className="text-[10px] sm:text-xs text-muted-foreground truncate max-w-[140px] sm:max-w-none">
                                  Entry: ${pos.entry_price_usd?.toFixed(8) || pos.entry_price?.toFixed(8) || "0.00000000"} · {asset}
                                </p>
                                <p className="text-[10px] text-muted-foreground/80 font-mono truncate max-w-[140px] sm:max-w-none" data-testid="position-open-timestamp">
                                  {fmtDateTime(pos.entry_timestamp || pos.opened_at || pos.open_timestamp)}
                                  <span className="text-primary/80">{botTypeSuffix(bot.bot_type || pos.bot_type)}</span>
                                </p>
                              </div>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleClosePosition(pos.id)}
                                disabled={positionLoading}
                                className="text-xs h-7 px-2"
                              >
                                Close
                              </Button>
                            </div>
                          </div>
                          );
                        })
                      : <p className="text-center text-muted-foreground text-sm">No open positions</p>
                    }
                  </CardContent>
                </Card>
              </div>

              {/* Closed Positions */}
              <div>
                <h4 className="text-sm font-medium mb-2">Recent Closed ({bot.open_positions?.filter(p => p.status === "closed").length || 0})</h4>
                <Card className="bg-muted/30 border-border">
                  <CardContent className="p-4">
                    {bot.open_positions?.filter(p => p.status === "closed").slice(0, 5).length > 0 
                      ? bot.open_positions.filter(p => p.status === "closed").slice(0, 5).map(pos => {
                          const asset = pos.price_asset || "STXOSHI";
                          const rawPct = pos.pnl_pct || 0;
                          const pctReliable = Math.abs(rawPct) < 100;
                          return (
                          <div key={pos.id} className="flex justify-between items-center py-2 border-b border-border last:border-0 opacity-60">
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <span className="text-xs flex items-center gap-1.5">
                                {pos.side?.toUpperCase()}
                                <span className="px-1 py-0.5 rounded text-[9px] font-semibold bg-muted text-foreground/70">{asset}</span>
                              </span>
                              <span className="text-[10px] text-muted-foreground/80 font-mono truncate" data-testid="closed-position-timestamp">
                                {fmtDateTime(pos.exit_timestamp || pos.close_timestamp || pos.closed_at || pos.entry_timestamp)}
                                <span className="text-primary/80">{botTypeSuffix(bot.bot_type || pos.bot_type)}</span>
                              </span>
                            </div>
                            {pctReliable ? (
                              <span className={`text-xs font-mono ${rawPct >= 0 ? "text-green-400" : "text-red-400"}`}>
                                {rawPct >= 0 ? "+" : ""}{rawPct.toFixed(2)}%
                              </span>
                            ) : (
                              <span className="text-xs font-mono text-muted-foreground">—</span>
                            )}
                          </div>
                          );
                        })
                      : <p className="text-center text-muted-foreground text-sm">No closed positions</p>
                    }
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Positions Value Card */}
          <Card className="glass-card border-border">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-semibold">{bot.name} Balance</h3>
                {bot.status === "running" && (
                  <span className="ml-auto flex items-center gap-1.5 text-[10px] text-muted-foreground" data-testid="bot-balance-live">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                    </span>
                    Live · updates each tick
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Total Balance:</p>
              <p className="font-mono text-2xl font-bold" data-testid="bot-total-balance">
                {(((bot.total_balance_stxoshi ?? bot.available_balance_stxoshi) || 0) / 1_000_000).toLocaleString()} STXOSHI
              </p>

              {/* Locked vs Free breakdown */}
              {(() => {
                const total = ((bot.total_balance_stxoshi ?? bot.available_balance_stxoshi) || 0) / 1_000_000;
                const locked = (bot.locked_balance_stxoshi || 0) / 1_000_000;
                const free = (bot.available_balance_stxoshi || 0) / 1_000_000;
                const lockedPct = total > 0 ? Math.min(100, (locked / total) * 100) : 0;
                const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });
                return (
                  <div className="mt-3">
                    {/* proportion bar */}
                    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" data-testid="bot-funds-bar">
                      <div className="h-full bg-green-500/70" style={{ width: `${100 - lockedPct}%` }} />
                      <div className="h-full bg-amber-500/70" style={{ width: `${lockedPct}%` }} />
                    </div>
                    <div className="mt-2 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between gap-2" data-testid="bot-free-funds">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500/70" />
                          Free (available)
                        </span>
                        <span className="font-mono text-green-400">{fmt(free)} STXOSHI</span>
                      </div>
                      <div className="flex items-center justify-between gap-2" data-testid="bot-locked-funds">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500/70" />
                          Locked in {bot.open_positions?.filter(p => p.status === "open").length || 0} open position{(bot.open_positions?.filter(p => p.status === "open").length || 0) === 1 ? "" : "s"}
                        </span>
                        <span className="font-mono text-amber-400">{fmt(locked)} STXOSHI</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {bot.total_pnl_stxoshi !== undefined && (
                <p className={`text-sm mt-3 ${(bot.total_pnl_stxoshi || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                  Total P&L: {((bot.total_pnl_stxoshi || 0) / 1_000_000).toLocaleString()} STXOSHI
                </p>
              )}

              {/* P&L split: bot auto-trading vs your protected manual positions */}
              {(() => {
                const open = (bot.open_positions || []).filter(p => p.status === "open");
                const manualOpen = open.filter(p => p.manual);
                if (manualOpen.length === 0) return null;
                const manualPnl = manualOpen.reduce((s, p) => s + (p.pnl_stxoshi || 0), 0) / 1_000_000;
                const total = (bot.total_pnl_stxoshi || 0) / 1_000_000;
                const botPnl = total - manualPnl;
                const row = (label, val, testid) => (
                  <div className="flex justify-between gap-2" data-testid={testid}>
                    <span className="text-muted-foreground">{label}</span>
                    <span className={`font-mono ${val >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {val >= 0 ? "+" : ""}{val.toLocaleString(undefined, { maximumFractionDigits: 3 })} STXOSHI
                    </span>
                  </div>
                );
                return (
                  <div className="mt-2 rounded-lg border border-border/50 bg-muted/20 p-2 space-y-1 text-xs" data-testid="bot-pnl-split">
                    {row("Bot auto-trading P&L", botPnl, "bot-auto-pnl")}
                    {row(`Manual positions P&L (${manualOpen.length})`, manualPnl, "bot-manual-pnl")}
                  </div>
                );
              })()}
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1 text-xs">
                <div className="flex justify-between gap-2" data-testid="bot-opened-balance">
                  <span className="text-muted-foreground">Opened Balance</span>
                  <span className="font-mono">
                    {(((bot.initial_deposit_stxoshi && bot.initial_deposit_stxoshi > 0)
                      ? bot.initial_deposit_stxoshi
                      : ((bot.total_balance_stxoshi || bot.available_balance_stxoshi || 0) - (bot.total_pnl_stxoshi || 0))
                    ) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 3 })} STXOSHI
                  </span>
                </div>
                <div className="flex justify-between gap-2" data-testid="bot-opened-at">
                  <span className="text-muted-foreground">Opened</span>
                  <span className="font-mono">{fmtDateTime(bot.opened_at || bot.created_at)}</span>
                </div>
                {bot.stopped_at && (
                  <div className="flex justify-between gap-2" data-testid="bot-closed-at">
                    <span className="text-muted-foreground">Closed</span>
                    <span className="font-mono">{fmtDateTime(bot.stopped_at)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Full Trade History (append-only from position_history collection) */}
          {bot?.id && <BotAnalyticsPanel botId={bot.id} />}
          {bot?.id && <BotTradeHistory botId={bot.id} botName={bot.name} botType={bot.bot_type} />}

          {/* Settings Panel (Slide Down) */}
          {showSettings && (
            <Card ref={settingsRef} className="glass-card border-primary/30 animate-fade-in" data-testid="bot-settings-panel">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Bot Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Kill Switch (Stop Loss)</Label>
                    <span className="font-mono text-red-400">{killSwitchPct}%</span>
                  </div>
                  <Slider
                    value={[Math.abs(killSwitchPct)]}
                    onValueChange={([v]) => setKillSwitchPct(-v)}
                    min={1}
                    max={50}
                    step={1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Bot stops trading if P&L drops below {killSwitchPct}%
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Take Profit</Label>
                    <span className="font-mono text-green-400">+{takeProfitPct}%</span>
                  </div>
                  <Slider
                    value={[takeProfitPct]}
                    onValueChange={([v]) => setTakeProfitPct(v)}
                    min={1}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Bot takes profit and resets at +{takeProfitPct}%
                  </p>
                </div>

                {bot.status === "running" && (
                  <Button
                    variant="destructive"
                    onClick={handleStopBot}
                    disabled={actionLoading}
                    className="w-full"
                    data-testid="bot-settings-stop"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Stop &amp; Close Out Bot
                  </Button>
                )}

                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowSettings(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateSettings}
                    disabled={actionLoading}
                    className="flex-1 bg-primary"
                  >
                    {actionLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </main>

      {/* Bottom spacer */}
      <div className="h-20" />
    </div>
  );
}
